import type { RowId, WhalePrice, WhaleRow, WhaleTier } from "@/lib/odds-screen/live/types";
import { getWhaleWatchFingerprint } from "@/lib/dismissal-fingerprints";
import { chosenGameDays, formatLocalYmd, gameTimeMatcher, startTimeChoices, type TimeChoices } from "@/lib/game-date";
import { normalizeSearchText, searchMatcher } from "@/lib/odds-screen/live/derive";
import {
  formatPlayName,
  formatSportLabel,
  getBookDisplayName,
  normalizeWhaleWatchBookKey,
  type MarketOpportunity,
  type ListPeer,
  type Side,
} from "@/lib/whale-watch/whale-watch-shared";
import { sideDisplayName } from "./whale-watch-api-mapping";
import { FILTER_DEFAULTS, isWithinOddsBounds, type WhaleWatchFilterState } from "./whale-watch-filters";

const DEFAULT_TIER_MIN_EDGE = 0;
const DEFAULT_TIER_MIN_WHALE = 100;

export function whaleTierFor(minEdge: number, minWhaleVolume: number): WhaleTier {
  return minEdge < DEFAULT_TIER_MIN_EDGE || minWhaleVolume < DEFAULT_TIER_MIN_WHALE ? "wide" : "default";
}

export type BookScope = {
  readonly books: ReadonlySet<string> | null;
  readonly minPlaySize: number;
};

export type WhaleFoldFilters = Pick<
  WhaleWatchFilterState,
  | "sportFilter"
  | "marketTypeFilter"
  | "selectedMarketNames"
  | "gameTime"
  | "minEdge"
  | "minWhaleVolume"
  | "minOdds"
  | "maxOdds"
  | "searchQuery"
  | "sortMode"
>;

const bookKeys = new Map<string, string>();

function bookKey(book: string): string {
  let key = bookKeys.get(book);
  if (key === undefined) {
    key = normalizeWhaleWatchBookKey(book);
    bookKeys.set(book, key);
  }
  return key;
}

export function bookScope(books: ReadonlySet<string> | null, minPlaySize: number): BookScope {
  return { books: books === null ? null : new Set([...books].map(bookKey)), minPlaySize };
}

export function scopedBooks(
  selectedBookKeys: readonly string[],
  availableBooks: readonly string[],
  emptyMeansAll: boolean,
  isHere: (book: string) => boolean,
): ReadonlySet<string> | null {
  const all = emptyMeansAll && selectedBookKeys.length === 0;
  const keys = [...new Set((all ? availableBooks : selectedBookKeys).map(bookKey))];
  const on = keys.filter(isHere);
  return all && on.length === keys.length ? null : new Set(on);
}

function meetsPlaySize(price: WhalePrice, minPlaySize: number): boolean {
  return !(minPlaySize > 0) || price.size === null || price.size >= minPlaySize;
}

const topPicks = new WeakMap<WhaleRow, WhalePrice | null>();

function topPick(row: WhaleRow): WhalePrice | null {
  if (topPicks.has(row)) return topPicks.get(row) ?? null;
  const listed = row.recommended_side.prices.find((price) => price.book === row.best_book);
  const pick = listed === undefined ? null : {
    ...listed,
    odds_american: row.best_odds,
    edge_percent: row.edge_percent,
    edge_supporting_liquidity: row.edge_supporting_liquidity,
  };
  topPicks.set(row, pick);
  return pick;
}

const scopedPicks = new WeakMap<WhaleRow, { readonly scope: BookScope; readonly pick: WhalePrice | null }>();

function cachedPick(row: WhaleRow, scope: BookScope): WhalePrice | null {
  const hit = scopedPicks.get(row);
  if (hit !== undefined && hit.scope === scope) return hit.pick;
  const pick = pickPrice(row, scope);
  scopedPicks.set(row, { scope, pick });
  return pick;
}

function beats(price: WhalePrice, pick: WhalePrice | null, bestBook: string): boolean {
  if (pick === null || price.odds_american !== pick.odds_american) return pick === null || price.odds_american > pick.odds_american;
  if (Boolean(price.bet_link) !== Boolean(pick.bet_link)) return Boolean(price.bet_link);
  return price.book === bestBook && pick.book !== bestBook;
}

export function pickPrice(row: WhaleRow, scope: BookScope): WhalePrice | null {
  if (scope.books === null) {
    const top = topPick(row);
    if (top !== null && meetsPlaySize(top, scope.minPlaySize)) return top;
  }
  let pick: WhalePrice | null = null;
  for (const price of row.recommended_side.prices) {
    if (scope.books !== null && !scope.books.has(bookKey(price.book))) continue;
    if (!meetsPlaySize(price, scope.minPlaySize)) continue;
    if (beats(price, pick, row.best_book)) pick = price;
  }
  return pick;
}

type Facts = {
  readonly startMs: number;
  readonly sport: string;
  readonly marketName: string;
  readonly kind: "game" | "player" | "other";
  readonly books: readonly string[];
};

type Labels = {
  readonly names: readonly [string, string];
  readonly fingerprint: string;
  readonly haystack: string;
};

const rowFacts = new WeakMap<WhaleRow, Facts>();
const rowLabels = new WeakMap<WhaleRow, Labels>();

function lowerSide(side: WhaleRow["recommended_side"]["side"]): Side["side"] {
  return side.toLowerCase() as Side["side"];
}

function numberSearchVariants(n: number): string[] {
  if (!Number.isFinite(n)) return [];
  const abs = Math.abs(n);
  return [String(n), String(abs), n.toFixed(1), abs.toFixed(1)];
}

function facts(row: WhaleRow): Facts {
  const hit = rowFacts.get(row);
  if (hit !== undefined) return hit;
  const md = row.market_data;
  const books = new Set<string>();
  for (const price of row.recommended_side.prices) books.add(bookKey(price.book));
  for (const book of row.opposite_side.peer_books) books.add(bookKey(book));
  books.delete("");
  const next: Facts = {
    startMs: Date.parse(md.event_start),
    sport: md.sport.toLowerCase(),
    marketName: md.market.toLowerCase(),
    kind: md.market_type === "player" || md.market_type === "player_prop"
      ? "player"
      : md.market_type === "game" ? "game" : "other",
    books: [...books],
  };
  rowFacts.set(row, next);
  return next;
}

function labels(row: WhaleRow): Labels {
  const hit = rowLabels.get(row);
  if (hit !== undefined) return hit;
  const md = row.market_data;
  const recommended = row.recommended_side;
  const opposite = row.opposite_side;
  const names = [
    sideDisplayName(lowerSide(recommended.side), md),
    sideDisplayName(lowerSide(opposite.side), md),
  ] as const;
  const playSide = { name: names[0], line: recommended.line, side: lowerSide(recommended.side) } as Side;
  const fingerprint = getWhaleWatchFingerprint(
    { sport: md.sport, league: md.league, event_id: md.event_id, market: { market: md.market, line: md.line } } as MarketOpportunity,
    playSide,
  ) ?? "";
  const haystack = [
    md.event, md.league, md.sport, formatSportLabel(md.sport), md.home_team, md.away_team, md.market,
    names[0], formatPlayName(playSide, md.market, md.market_arity),
    ...numberSearchVariants(md.line), ...numberSearchVariants(recommended.line), ...numberSearchVariants(opposite.line),
  ].map((value) => String(value ?? "").toLowerCase()).join("\u0000");
  const next: Labels = { names, fingerprint, haystack };
  rowLabels.set(row, next);
  return next;
}

function peersFrom(prices: readonly WhalePrice[]): ListPeer[] {
  return prices.map((price) => ({
    book: price.book,
    odds_american: price.odds_american,
    limit: price.size ?? undefined,
    bet_link: price.bet_link || undefined,
    bet_link_mobile: price.bet_link_mobile || undefined,
    edge_percent: price.edge_percent,
    fee_adjusted: price.fee_adjusted,
  }));
}

export function leanRowToOpportunity(row: WhaleRow, pick: WhalePrice): MarketOpportunity {
  const md = row.market_data;
  const recommended = row.recommended_side;
  const opposite = row.opposite_side;
  const { names } = labels(row);
  const sides: Side[] = [
    {
      name: names[0],
      line: recommended.line,
      side: lowerSide(recommended.side),
      total_liquidity: 0,
      actual_money_at_risk: recommended.money_at_risk,
      pinnacle_limit: 0,
      books: {},
      peers: peersFrom(recommended.prices),
      best_odds: pick.odds_american,
      best_book: pick.book,
      best_bet_link: pick.bet_link || undefined,
      best_bet_link_mobile: pick.bet_link_mobile || undefined,
      edge_percent: pick.edge_percent,
      demand: recommended.demand,
    },
    {
      name: names[1],
      line: opposite.line,
      side: lowerSide(opposite.side),
      total_liquidity: 0,
      actual_money_at_risk: opposite.money_at_risk,
      pinnacle_limit: 0,
      books: {},
      peers: [],
      peer_books: opposite.peer_books,
      best_odds: 0,
      best_book: "",
      demand: opposite.demand,
    },
  ];
  return {
    id: md.id,
    event_id: md.event_id,
    event: md.event,
    event_start: md.event_start,
    home_team: md.home_team,
    away_team: md.away_team,
    league: md.league,
    sport: md.sport,
    market_type: md.market_type,
    updated_at: md.updated_at,
    entity_type: md.entity_type,
    entity_name: md.entity_name,
    image_url: md.image_url,
    home_logo_url: md.home_logo_url,
    away_logo_url: md.away_logo_url,
    market: {
      market: md.market,
      line: md.line,
      sides,
      total_liquidity: recommended.money_at_risk === undefined || opposite.money_at_risk === undefined ? undefined : recommended.money_at_risk + opposite.money_at_risk,
      pinnacle_limit: 0,
      edge_supporting_liquidity: pick.edge_supporting_liquidity,
      same_side_conviction_liquidity: row.same_side_conviction_liquidity,
      market_arity: md.market_arity,
      fair_outcome_share: row.fair_outcome_share,
    },
  };
}

type Display = { readonly pick: WhalePrice; readonly opp: MarketOpportunity };
const displays = new WeakMap<WhaleRow, Display>();

function display(row: WhaleRow, pick: WhalePrice): MarketOpportunity {
  const hit = displays.get(row);
  if (hit !== undefined && hit.pick === pick) return hit.opp;
  const opp = leanRowToOpportunity(row, pick);
  displays.set(row, { pick, opp });
  return opp;
}

type Kept = { readonly id: RowId; readonly edge: number | undefined; readonly whale: number };

type FoldBasis = {
  readonly rows: ReadonlyMap<RowId, WhaleRow>;
  readonly scope: BookScope;
  readonly hidden: ReadonlySet<string>;
  readonly filtersKey: string;
  readonly clockMs: number | null;
  readonly kept: readonly Kept[];
};

export type WhaleFold = {
  readonly ids: readonly RowId[];
  readonly rowAt: (id: RowId) => MarketOpportunity | null;
  readonly basis: FoldBasis | null;
};

function rowReader(rows: ReadonlyMap<RowId, WhaleRow>, scope: BookScope | null) {
  return (id: RowId): MarketOpportunity | null => {
    const row = rows.get(id);
    const pick = row === undefined || scope === null ? null : cachedPick(row, scope);
    return row === undefined || pick === null ? null : display(row, pick);
  };
}

function sportKey(sportFilter: string): string | null {
  return sportFilter === "all" ? null : sportFilter.toLowerCase();
}

function keeper(scope: BookScope, filters: WhaleFoldFilters, hidden: ReadonlySet<string>, nowMs: number) {
  const query = filters.searchQuery.trim().toLowerCase();
  const names = /\d/.test(normalizeSearchText(query)) ? null : searchMatcher(query);
  const sport = sportKey(filters.sportFilter);
  const markets = filters.selectedMarketNames.length === 0
    ? null
    : new Set(filters.selectedMarketNames.map((name) => name.toLowerCase()));
  const startsInTime = gameTimeMatcher(filters.gameTime, new Date(nowMs));
  return (id: RowId, row: WhaleRow): Kept | null => {
    const known = facts(row);
    if (sport !== null && known.sport !== sport) return null;
    if (filters.marketTypeFilter !== "all" && known.kind !== filters.marketTypeFilter) return null;
    if (markets !== null && !markets.has(known.marketName)) return null;
    if (startsInTime !== null && !startsInTime(known.startMs)) return null;
    if (hidden.size > 0 && hidden.has(labels(row).fingerprint)) return null;
    const pick = cachedPick(row, scope);
    if (pick === null) return null;
    const edge = pick.edge_percent;
    if (edge === undefined ? filters.minEdge > FILTER_DEFAULTS.minEdge : edge < filters.minEdge) return null;
    const whale = edge !== undefined && edge >= 0 ? pick.edge_supporting_liquidity : row.same_side_conviction_liquidity;
    if (whale < filters.minWhaleVolume) return null;
    if (!isWithinOddsBounds(pick.odds_american, filters.minOdds, filters.maxOdds)) return null;
    if (query && !labels(row).haystack.includes(query) && !names?.(row.market_data.event, row.market_data.home_team, row.market_data.away_team, row.market_data.entity_name)) return null;
    return { id, edge, whale };
  };
}

function edgeDescending(a: Kept, b: Kept): number {
  if (a.edge === undefined || b.edge === undefined) return (a.edge === undefined ? 1 : 0) - (b.edge === undefined ? 1 : 0);
  return b.edge - a.edge;
}

function comparer(sortMode: WhaleFoldFilters["sortMode"]) {
  const byEdge = sortMode === "edge";
  return (a: Kept, b: Kept) => {
    const first = byEdge ? edgeDescending(a, b) : b.whale - a.whale;
    if (first !== 0) return first;
    const second = byEdge ? b.whale - a.whale : edgeDescending(a, b);
    if (second !== 0) return second;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  };
}

function merge(left: readonly Kept[], right: readonly Kept[], compare: (a: Kept, b: Kept) => number): Kept[] {
  const out: Kept[] = new Array(left.length + right.length);
  let i = 0;
  let j = 0;
  let k = 0;
  while (i < left.length && j < right.length) out[k++] = compare(left[i], right[j]) <= 0 ? left[i++] : right[j++];
  while (i < left.length) out[k++] = left[i++];
  while (j < right.length) out[k++] = right[j++];
  return out;
}

type WhaleFoldInput = {
  readonly rows: ReadonlyMap<RowId, WhaleRow>;
  readonly scope: BookScope | null;
  readonly filters: WhaleFoldFilters;
  readonly hidden: ReadonlySet<string>;
  readonly nowMs: number;
};

export function foldWhaleRows(input: WhaleFoldInput, previous: WhaleFold | null = null): WhaleFold {
  const { rows, scope, filters, hidden, nowMs } = input;
  const rowAt = rowReader(rows, scope);
  if (scope === null) return { ids: [], rowAt, basis: null };
  const filtersKey = JSON.stringify([filters, chosenGameDays(filters.gameTime, formatLocalYmd(new Date(nowMs)))]);
  const clockMs = filters.gameTime.within > 0 ? nowMs : null;
  const keep = keeper(scope, filters, hidden, nowMs);
  const compare = comparer(filters.sortMode);
  const basis = previous?.basis;
  let kept: Kept[];
  if (basis && basis.scope === scope && basis.hidden === hidden && basis.filtersKey === filtersKey && basis.clockMs === clockMs) {
    const stale = new Set<RowId>();
    for (const [id, row] of basis.rows) if (rows.get(id) !== row) stale.add(id);
    const fresh: Kept[] = [];
    for (const [id, row] of rows) {
      if (basis.rows.get(id) === row) continue;
      const entry = keep(id, row);
      if (entry !== null) fresh.push(entry);
    }
    fresh.sort(compare);
    kept = merge(stale.size === 0 ? basis.kept : basis.kept.filter((entry) => !stale.has(entry.id)), fresh, compare);
  } else {
    kept = [];
    for (const [id, row] of rows) {
      const entry = keep(id, row);
      if (entry !== null) kept.push(entry);
    }
    kept.sort(compare);
  }
  return { ids: kept.map((entry) => entry.id), rowAt, basis: { rows, scope, hidden, filtersKey, clockMs, kept } };
}

export function createWhaleFold(): (input: WhaleFoldInput) => WhaleFold {
  let last: WhaleFold | null = null;
  return (input) => (last = foldWhaleRows(input, last));
}

export function whaleTimeChoices(rows: ReadonlyMap<RowId, WhaleRow>, sportFilter: string, todayYmd: string, nowMs: number): TimeChoices {
  const sport = sportKey(sportFilter);
  const starts = new Map<number, number>();
  for (const row of rows.values()) {
    const known = facts(row);
    if (sport !== null && known.sport !== sport) continue;
    starts.set(known.startMs, (starts.get(known.startMs) ?? 0) + 1);
  }
  return startTimeChoices(starts, todayYmd, nowMs);
}

export type WhaleRowOptions = {
  readonly sports: string[];
  readonly marketsByType: { readonly all: string[]; readonly game: string[]; readonly player: string[] };
  readonly books: string[];
};

export function createWhaleOptionsIndex(): (rows: ReadonlyMap<RowId, WhaleRow>) => WhaleRowOptions {
  let seen: ReadonlyMap<RowId, WhaleRow> = new Map();
  const sports = new Map<string, number>();
  const game = new Map<string, number>();
  const player = new Map<string, number>();
  const other = new Map<string, number>();
  const books = new Map<string, number>();
  let options: WhaleRowOptions = { sports: [], marketsByType: { all: [], game: [], player: [] }, books: [] };
  let changed = false;
  const bump = (counts: Map<string, number>, key: string, delta: number) => {
    const next = (counts.get(key) ?? 0) + delta;
    if (next > 0 && !counts.has(key)) changed = true;
    if (next <= 0) {
      changed = true;
      counts.delete(key);
    } else {
      counts.set(key, next);
    }
  };
  const count = (row: WhaleRow, delta: number) => {
    const known = facts(row);
    bump(sports, known.sport, delta);
    bump(known.kind === "player" ? player : known.kind === "game" ? game : other, known.marketName, delta);
    for (const book of known.books) bump(books, book, delta);
  };
  return (rows) => {
    if (rows === seen) return options;
    for (const [id, row] of seen) if (rows.get(id) !== row) count(row, -1);
    for (const [id, row] of rows) if (seen.get(id) !== row) count(row, 1);
    seen = rows;
    if (!changed) return options;
    changed = false;
    const keep = (previous: string[], next: string[]) =>
      previous.length === next.length && previous.every((value, index) => value === next[index]) ? previous : next;
    const sorted = (counts: Map<string, number>) => [...counts.keys()].sort();
    const next = {
      sports: keep(options.sports, sorted(sports)),
      marketsByType: {
        all: keep(options.marketsByType.all, [...new Set([...game.keys(), ...player.keys(), ...other.keys()])].sort()),
        game: keep(options.marketsByType.game, sorted(game)),
        player: keep(options.marketsByType.player, sorted(player)),
      },
      books: keep(options.books, [...books.keys()].sort((a, b) => getBookDisplayName(a).localeCompare(getBookDisplayName(b)))),
    };
    const same = next.sports === options.sports && next.books === options.books &&
      next.marketsByType.all === options.marketsByType.all && next.marketsByType.game === options.marketsByType.game &&
      next.marketsByType.player === options.marketsByType.player;
    if (!same) options = next;
    return options;
  };
}
